<template>
  <router-view @created="refetch" />
  <Flex
    v-show="!showEmptyPageContent && firstFetchFinished"
    justify="between"
    :align="$screen === 's' ? null : 'end'"
    :direction="$screen === 's' ? 'column' : 'row'"
  >
    <Flex
      :direction="$screen === 's' ? 'column' : 'row'"
      :align="$screen === 's' ? 'stretch' : 'end'"
    >
      <BaseDatePicker
        v-if="queryVariables && 'date' in queryVariables"
        v-model="variables.date"
        :label="$t('global.actions.filter_item', { item: $t('global.date') })"
        v-test="'actions-date'"
        resettable
      />
      <BaseDropdown
        v-if="dropdownItems && dropdownItems.length"
        v-model="variables.status"
        :options="dropdownItems"
        v-test="'actions-dropdown'"
      />
      <BaseSearch
        v-if="!hideSearch"
        debounce
        v-model="variables.search"
      />
    </Flex>
    <BaseButton
      :routerLink="{ name: props.actions.route }"
      v-test="'actions-create'"
    >
      {{ $t(props.actions.text) }}
    </BaseButton>
  </Flex>

  <BaseSpinner v-if="loading && !firstFetchFinished" />

  <BaseCard v-if="firstFetchFinished && items.length">
    <slot></slot>
  </BaseCard>

  <LoadMore
    v-show="firstFetchFinished && !allDataFetched"
    @shown="fetchMore"
  />

  <EmptyPageContent
    v-show="showEmptyPageContent || noResults"
    :imageName="emptyPageContent.imageName"
    :text="
      showEmptyPageContent
        ? {
            title: emptyPageContent.title,
            description: emptyPageContent.description,
            btnPrimary: emptyPageContent.btnPrimary
          }
        : undefined
    "
    @click="onEmptyPageClick"
  />
</template>

<script
  setup
  lang="ts"
>
import type { DocumentNode } from 'graphql';
import { provide } from 'vue';
import { useRouter } from 'vue-router';
import { usePagination } from '@/apollo/pagination';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import LoadMore from '@/components/LoadMore.vue';

const router = useRouter();

interface EmptyPage {
  title: string;
  description: string;
  btnPrimary: string;
  route?: string;
  imageName: string;
}

interface Actions {
  text: string;
  route: string;
}

interface QueryVariables {
  status?: string;
  type?: string;
  date?: string | null;
}

const props = defineProps<{
  query: DocumentNode;
  multiLocation?: boolean;
  emptyPageContent: EmptyPage;
  actions: Actions;
  dropdownItems?: Array<{ value: string | number; label: string }>;
  queryVariables?: QueryVariables;
  hideSearch?: boolean;
}>();

const {
  items,
  variables,
  allDataFetched,
  loading,
  firstFetchFinished,
  fetchMore,
  refetch,
} = usePagination({
  fieldName: 'items',
  query: props.query,
  variables: {
    search: '',
    ...props.queryVariables,
  },
  options: {
    multiLocation: props.multiLocation,
  },
});

const emit = defineEmits(['fetch']);

provide('page-items', items);

const noResults = computed(
  () => !loading.value && (!items.value || items.value.length === 0),
);

const showEmptyPageContent = computed(
  () =>
    noResults.value &&
    firstFetchFinished.value &&
    !variables.search &&
    !variables.status &&
    !variables.date,
);

const onEmptyPageClick = () => {
  router.push({ name: props.emptyPageContent.route });
};
</script>
